import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Loader2, Phone, Mail } from "lucide-react";
import { FunnelStats } from "@/components/FunnelStats";
import { LeadNotes, FollowUpBadge } from "@/components/LeadNotes";

const TEMP_STYLE = { hot: "bg-rose-100 text-rose-700", warm: "bg-amber-100 text-amber-800", cold: "bg-slate-100 text-slate-600" };
const STATUSES = ["New", "Contacted", "Qualified", "Converted", "Lost"];
const SEGMENTS = ["all", "student", "employer", "expert"];

const toCsv = (rows) => {
  const cols = ["segment", "temperature", "score", "status", "channel", "name", "email", "phone", "city", "organisation", "answers", "follow_up_date", "notes", "created_at"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const val = (r, c) => {
    if (c === "answers") return Object.entries(r.answers || {}).map(([k, v]) => `${k}:${v}`).join("; ");
    if (c === "notes") return (r.notes || []).map((n) => `[${n.at.slice(0, 10)}] ${n.text}`).join(" | ");
    return r[c];
  };
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(val(r, c))).join(","))].join("\n");
};

const LeadRow = ({ lead, t, onStatus, onChange }) => (
  <Card className="border-slate-200 p-5" data-testid="funnel-lead-row">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-heading text-lg font-bold text-slate-900">{lead.name}</p>
          <FollowUpBadge date={lead.follow_up_date} t={t} />
          <Badge className={TEMP_STYLE[lead.temperature]} data-testid="funnel-lead-temp">{t(`admin.funnel${lead.temperature[0].toUpperCase()}${lead.temperature.slice(1)}`)} · {lead.score}</Badge>
          <Badge variant="outline">{t(`admin.funnelSeg_${lead.segment}`)}</Badge>
          <Badge className="max-w-[220px] truncate bg-sky-100 text-sky-800" title={Object.entries(lead.source || {}).map(([k, v]) => `${k}: ${v}`).join("\n")} data-testid="funnel-lead-channel">{t("admin.funnelSource")}: {lead.channel || "direct"}</Badge>
        </div>
        <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
          <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 hover:text-amber-700"><Phone className="h-3.5 w-3.5" /> {lead.phone}</a>
          <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-amber-700"><Mail className="h-3.5 w-3.5" /> {lead.email}</a>
          {lead.city && <span>{lead.city}</span>}
          {lead.organisation && <span className="font-medium">{lead.organisation}</span>}
        </p>
      </div>
      <Select value={lead.status} onValueChange={(v) => onStatus(lead.lead_id, v)}>
        <SelectTrigger className="w-40" data-testid="funnel-lead-status"><SelectValue /></SelectTrigger>
        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div className="mt-3 flex flex-wrap gap-2">
      {Object.entries(lead.answers || {}).map(([k, v]) => (
        <span key={k} className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700"><span className="font-semibold">{k}:</span> {v.replace(/_/g, " ")}</span>
      ))}
      <span className="ml-auto text-xs text-slate-400">{new Date(lead.created_at).toLocaleString()}</span>
    </div>
    {lead.integrations && (
      <div className="mt-2 flex flex-wrap gap-2 text-xs" data-testid="funnel-lead-integrations">
        {Object.entries(lead.integrations).map(([k, v]) => (
          <span key={k} className={`rounded-md px-2 py-0.5 font-semibold ${v === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} title={v}>{k === "aisensy" ? "WhatsApp" : "Corefactors"}: {v === "sent" ? "sent" : "failed"}</span>
        ))}
      </div>
    )}
    <LeadNotes lead={lead} t={t} onChange={(patch) => onChange(lead.lead_id, patch)} />
  </Card>
);

export const FunnelLeadsTab = () => {
  const { t } = useTranslation();
  const [seg, setSeg] = useState("all");
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    setLeads(null);
    api.get("/admin/funnel-leads", { params: seg === "all" ? {} : { segment: seg } }).then(({ data }) => setLeads(data)).catch(() => setLeads([]));
  }, [seg]);
  const onStatus = async (id, status) => {
    try {
      await api.patch(`/admin/funnel-leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.lead_id === id ? { ...l, status } : l)));
      toast.success(t("admin.funnelUpdated"));
    } catch {
      toast.error(t("funnel.common.error"));
    }
  };
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(leads || [])], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `funnel-leads-${seg}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4" data-testid="funnel-leads-tab">
      <FunnelStats />
      <div className="flex flex-wrap items-center gap-2">
        {SEGMENTS.map((s) => (
          <Button key={s} size="sm" variant={seg === s ? "default" : "outline"} onClick={() => setSeg(s)} data-testid={`funnel-filter-${s}`}>
            {s === "all" ? t("admin.funnelAll") : t(`admin.funnelSeg_${s}`)}
          </Button>
        ))}
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportCsv} disabled={!leads?.length} data-testid="funnel-export">
          <Download className="mr-1.5 h-4 w-4" /> {t("admin.funnelExport")}
        </Button>
      </div>
      {leads === null && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>}
      {leads?.length === 0 && <p className="py-10 text-center text-slate-500" data-testid="funnel-empty">{t("admin.funnelEmpty")}</p>}
      {leads?.map((l) => <LeadRow key={l.lead_id} lead={l} t={t} onStatus={onStatus} onChange={(id, patch) => setLeads((ls) => ls.map((x) => (x.lead_id === id ? { ...x, ...patch } : x)))} />)}
    </div>
  );
};
